import React, { useMemo, useRef, useEffect } from 'react'
import PaginatedPage from '../../components/PaginatedPage'
import TableView from '../../components/TableView'
import BasicDateRenderer from '../../utils/renderers'
import VerificationPresenter from '../../api/verificationPresenter'

import './translations'
import VerificationUpsertForm from './VerificationUpsertForm'
import { Status, useWindowContext, noop } from '@iqueue/ui-kit'
import moment from 'moment'
import usePermissions from '../../components/usePermissions'
import { DevEUIFormatter } from '../sensor/SensorsPage'
import useTranslate, { t } from '../../hooks/useTranslate'

export function VerificationTable({ ...props }) {
  const { permissions } = usePermissions()
  const locale = useTranslate()

  const tableRef = useRef()

  const schema = useMemo(() => [{
    key: 'sensor.devEUI',
    title: t('verificationPage.table.devEUI'),
    render: (v) => {
      return <DevEUIFormatter devEUI={ v }/>
    },
  }, {
    key: 'sensor.serialNumber',
    title: t('verificationPage.table.serialNumber'),
    render: (v) => {
      return <span>{ v || '??' }</span>
    },
    width: '20rem',
  }, {
    key: 'description',
    title: t('verificationPage.table.description'),
    width: '20rem',
  }, {
    key: 'verifiedAt',
    title: t('verificationPage.table.verifiedAt'),
    render: BasicDateRenderer,
    width: '20rem',
  }, {
    key: 'status',
    title: t('verificationPage.table.status'),
    render: (_, item) => {
      const currentDate = moment()
      const verifiedAt = moment(item.verifiedAt)
      const expireAt = moment(item.expireAt)
      let statusProps = {
        progress: 0,
        error: false,
        remainingTime: '',
        warning: false,
      }

      if (currentDate.isAfter(expireAt)) {
        statusProps.error = true
        statusProps.progress = 100
        statusProps.remainingTime = moment(expireAt).format('L')
      } else {
        const duration = expireAt.diff(currentDate)
        const totalDays = expireAt.diff(currentDate, 'days')

        statusProps.progress = (duration / (expireAt.diff(verifiedAt))) * 100

        if (totalDays < 15) {
          statusProps.warning = true
        }
      }

      return <Status
        progress={ statusProps.error ? undefined : statusProps.progress }
        horizontal
        error={ statusProps.error }
        warning={ statusProps.warning }
      >
        { statusProps.remainingTime || moment(expireAt).format('L') }
      </Status>

    },
    width: '20rem',
  }, {
    key: 'createdAt',
    title: t('verificationPage.table.createdAt'),
    render: BasicDateRenderer,
    width: '20rem',
  }], [])

  return <TableView
    ref={ tableRef }
    permissions={ permissions[VerificationPresenter.key.toUpperCase()] }
    form={ VerificationUpsertForm }
    schema={ schema }
    entity={ 'verification' }
    minWidth={ '125rem' }
    { ...props }
  />
}

export default function VerificationPage() {
  const { setTitle, togglePaddings, toggleScrollbars } = useWindowContext()
  const locale = useTranslate()
  const translatedTitle = useMemo(() => t('menu.verification'), [locale])

  useEffect(() => {
    setTitle(translatedTitle)
    togglePaddings(false)
    toggleScrollbars(true)
  }, [translatedTitle])

  return <PaginatedPage
    presenter={ VerificationPresenter }
    component={ VerificationTable }
  />
}
